// Angular
import { Injectable } from '@angular/core';

// Libraries
import _uniqBy from 'lodash-es/uniqBy';

// Afidium
import { IGetEnumsService } from 'libraries/service/booking/enums/IGetEnums';
import { BookingType, ServiceCategory } from 'libraries/service/resources/booking/interfaces/get-bookings.enum';
import {
    IAccommodation,
    IGetBookingsAirResponse,
    IGetBookingsStayResponse,
    ISectorStay,
    IService,
    ISsr,
    ITraveller,
    IXId,
    TGetBookingResponse
} from 'libraries/service/resources/booking/interfaces/IGetBookings';
import {
    IServicesCellData,
    IServicesTableColumns,
    IServicesTableConfig,
    IServicesTableData,
    IServicesTableGroups, TServicesTableData
} from '../../components/services-table/IServicesTableConfig';
import { SectorAirServicesService } from '../sector-air-services/sector-air-services.service';
import { ISectorServicesConfig, IServicesConfig } from '../../interfaces/ISectorServicesConfig';
import { Yii } from 'libraries/interfaces/Yii';
import { SectorStayServicesService } from '../sector-stay-services/sector-stay-services.service';
import { ROOM_LABELS } from 'libraries/hotel/services/room-labels';
import { TravellerDataTranslationService } from "libraries/service/traveller-data-translation/traveller-data-translation.service";
import { SectorXType } from 'libraries/service/resources/model/search/air/IAirSector';

export interface INameXId extends IXId {
    name: string;
    info: string;
}

export interface IServicesListGetterInput {
    code: string;
    name: string;
    translation?: string;
    iterator?: IXId[];
    sector?: IXId[];
}

export interface ITableSectorServices {
    servicesList: IServicesListGetterInput[];
    enumService: IGetEnumsService[];
    listIterator: INameXId[];
    firstColumnGroup: IServicesTableGroups;
    servicesConfig?: IServicesConfig;
}

export interface ITableConfigGetterInput extends ITableSectorServices {
    sectorXId: string;
    type: SectorXType;
}

export interface ITableServices {
    air: ITableSectorServices;
    stay: ITableSectorServices;
}

@Injectable()
export class TableServicesService {

    private readonly displayableGroup: ServiceCategory[] = [
        ServiceCategory.accommodation,
        ServiceCategory.mealplan,
        ServiceCategory.luggage,
        ServiceCategory.seat,
        ServiceCategory.ancillary,
        ServiceCategory.animal,
        ServiceCategory.ticket,
        ServiceCategory.payment,
        ServiceCategory.unknown,
    ];

    constructor(
        private readonly sectorAirServicesService: SectorAirServicesService,
        private readonly sectorStayServicesService: SectorStayServicesService,
        private readonly travellerDataTranslationService: TravellerDataTranslationService,
    ) {
    }

    public getSectorTableConfig(
        { servicesList, enumService, listIterator, sectorXId, firstColumnGroup, servicesConfig, type }:
            ITableConfigGetterInput
    ): IServicesTableConfig {
        const serviceGroups: IServicesTableGroups[] = this.displayableGroup
            .map((category: ServiceCategory): IServicesTableGroups => {
                const serviceList: IGetEnumsService[] = enumService.filter((service: IGetEnumsService) => service.category.indexOf(category) > -1);
                const columns: IServicesTableColumns[] = getColumnForGroup(servicesList, serviceList, servicesConfig, type);
                return {
                    name: this.sectorAirServicesService.getServiceCategoryLabel(category) ||
                        this.sectorStayServicesService.getServiceCategoryLabel(category),
                    columns
                };
            })
            .filter((serviceGroup: IServicesTableGroups) => serviceGroup.columns.length > 0);

        const groups: IServicesTableGroups[] = [
            firstColumnGroup,
            ...serviceGroups
        ];

        const columnDefs: string[] = getColumnDefsList(groups);
        const tempdata: IServicesTableData[] = listIterator.map(
            (iterator: INameXId) => {
                return getServiceValuesPerIterator({
                    columnDefs,
                    iterator,
                    servicesList,
                    sectorXId,
                    firstColumnGroup,
                    servicesConfig
                });
            }
        );

        const data: TServicesTableData[] = tempdata.map((element: IServicesTableData) => {
            const mapContent: TServicesTableData = new Map();
            columnDefs.forEach((def: string) => mapContent.set(def, element[def]));
            return mapContent;
        });

        return {
            groups,
            data
        };
    }

    public getBookingTableServicesMapping(
        bookingData: TGetBookingResponse,
        servicesConfig: ISectorServicesConfig,
        serviceEnum: IGetEnumsService[]): ITableServices {
        return {
            air: bookingData.type && bookingData.type.some((type: BookingType) => type === BookingType.air) ?
                this.getBookingAirTableServicesMapping(bookingData as IGetBookingsAirResponse, servicesConfig, serviceEnum) :
                null,
            stay: bookingData.type && bookingData.type.some((type: BookingType) => type === BookingType.stay) ?
                this.getBookingStayTableServicesMapping(bookingData as IGetBookingsStayResponse, servicesConfig) :
                null
        };
    }

    private getBookingAirTableServicesMapping(
        bookingData: IGetBookingsAirResponse,
        servicesConfig: ISectorServicesConfig,
        serviceEnum: IGetEnumsService[]): ITableSectorServices {
        const firstColumnGroup: IServicesTableGroups = {
            name: Yii.t('wui-shared-js', 'Voyageurs'),
            columns: [
                {
                    columnDef: "PAX",
                    header: "",
                }
            ]
        };

        const listIterator: INameXId[] = (bookingData.traveller || []).map(
            (traveller: ITraveller): INameXId => ({
                name: this.travellerDataTranslationService.getTravellerIdentity(traveller),
                xId: traveller.xId,
                info: null
            })
        );

        const services: IGetEnumsService[] = this.displayableGroup
            .reduce((acc: IGetEnumsService[], category: ServiceCategory): IGetEnumsService[] =>
                ([
                    ...acc,
                    ...serviceEnum.filter((service: IGetEnumsService) => service.category && service.category.includes(category))
                ])
                , []);

        return {
            servicesList: (bookingData.ssr || [])
                .filter((ssr: ISsr) => services.some((enumsService: IGetEnumsService) => ssr.code === enumsService.code))
                .map((ssr: ISsr): IServicesListGetterInput => ({
                    code: ssr.code,
                    iterator: ssr.traveller,
                    name: ssr.name,
                    sector: ssr.sector
                })),
            listIterator,
            enumService: serviceEnum,
            firstColumnGroup,
            servicesConfig: servicesConfig[BookingType.air]
        };
    }

    private getBookingStayTableServicesMapping(booking: IGetBookingsStayResponse, servicesConfig: ISectorServicesConfig): ITableSectorServices {
        let servicesList: IServicesListGetterInput[] = [];
        const enumService: IGetEnumsService[] = [];
        const listIterator: INameXId[] = [];

        const { traveller } = booking;
        //to be sure to show old bookings
        const accomodations: IAccommodation[] = Array.isArray(booking.accomodation) ? booking.accomodation : [booking.accomodation];

        (accomodations || []).forEach((room: IAccommodation, index: number) => {
            // Categories Accommodation
            enumService.push(
                getAccommodationCategories(room),
            );

            // List iterator
            listIterator.push({
                name: `${Yii.t('wui-shared-js', 'Chambre')} ${index + 1}`,
                xId: room.xId,
                info: this.getTravellerInformations(room, booking, traveller)
            });

            // Services
            //to be sure to show old bookings
            const sectorXId: IXId[] = booking.sector
                .filter((sector: ISectorStay) =>
                    (Array.isArray(sector.accomodation) ? sector.accomodation : [sector.accomodation]).some((acco: IXId) => acco.xId === room.xId))
                .map((sector: ISectorStay) => ({ xId: sector.xId }));

            servicesList = getService(servicesList, room.code, room.name, sectorXId, room.xId);

            if (room.hasOwnProperty('service')) {
                booking.service.forEach((service: IService) => {
                    servicesList = getService(servicesList, service.code, service.name, sectorXId, room.xId);
                });
            }
        });

        // Categories Mealplan
        if (booking.service) {
            enumService.push(
                ...getMealplanCategories(booking)
            );
        }


        const firstColumnGroup: IServicesTableGroups = {
            name: Yii.t('wui-shared-js', 'Chambre'),
            columns: [
                {
                    columnDef: "ROOM",
                    header: "",
                }
            ]
        };

        return {
            servicesList,
            enumService,
            listIterator,
            servicesConfig: servicesConfig.stay,
            firstColumnGroup
        };
    }

    private getTravellerInformations(room: IAccommodation, booking: IGetBookingsStayResponse, traveller: ITraveller[]): string {
        let travellersInformations: string = '';

        (room.traveller || []).forEach((travellerInfo: IXId) => {
            const travellerFound: ITraveller = traveller.find((t: ITraveller) => t.xId === travellerInfo.xId);

            travellersInformations += `${this.travellerDataTranslationService.getTravellerIdentity(travellerFound)}<br>`;
        });

        return travellersInformations;
    }
}

function getService(
    servicesList: IServicesListGetterInput[], code: string, name: string,
    sectorXId: IXId[], accommodationXId: string): IServicesListGetterInput[] {

    if (servicesList.some((service: IServicesListGetterInput) => service.code === code)) {
        return servicesList.map((service: IServicesListGetterInput) => {
            if (service.code === code) {
                return {
                    ...service,
                    iterator: [
                        ...service.iterator.filter(({ xId }: { xId: string }) => xId !== accommodationXId),
                        { xId: accommodationXId }
                    ],
                    sector: [
                        ...service.sector.filter(({ xId }: { xId: string }) => sectorXId.some((sector: IXId) => sector.xId !== xId)),
                        ...sectorXId
                    ]
                };
            }

            return service;
        });
    }

    return [
        ...servicesList,
        { code, name, sector: sectorXId, iterator: [{ xId: accommodationXId }] }
    ];
}

function getAccommodationCategories(accommodation: IAccommodation): IGetEnumsService {
    return {
        category: [ServiceCategory.accommodation],
        code: accommodation.code,
        name: accommodation.xCode ? ROOM_LABELS[capitalize(accommodation.xCode)] : null,
    };
}

function getMealplanCategories(booking: IGetBookingsStayResponse): IGetEnumsService[] {
    //to be sure to show old bookings
    const services: IService[] = Array.isArray(booking.service) ? booking.service : [booking.service];

    return (services || [])
        .filter((service: IService) => service && service.hasOwnProperty('mealplan'))
        .map((service: IService) => ({
            category: [ServiceCategory.mealplan],
            code: service.code,
            name: service.name
        }));
}

function getServiceValuesPerIterator(
    { columnDefs, iterator, servicesList, sectorXId, firstColumnGroup, servicesConfig }:
        {
            columnDefs: string[];
            iterator: INameXId;
            servicesList: IServicesListGetterInput[];
            sectorXId: string;
            firstColumnGroup: IServicesTableGroups;
            servicesConfig: IServicesConfig;
        }): IServicesTableData {
    const fullServicesList: IServicesListGetterInput[] = [
        ...servicesList,
        ...Object.keys(servicesConfig)
            .filter((serviceName: string) =>
                servicesConfig[serviceName].isRequired &&
                !servicesList.some((serviceItem: IServicesListGetterInput) => serviceItem.code === serviceName))
            .map((serviceName: string) => ({
                code: serviceName,
                name: serviceName,
            }))
    ];
    return columnDefs.reduce(
        (acc: IServicesTableData, columnDef: string) => {
            const columnData: IServicesCellData = columnDef === firstColumnGroup.columns[0].columnDef ?
                getFirstColumnData(iterator) :
                getServiceTableData({
                    service: fullServicesList.filter((ssr: IServicesListGetterInput) => ssr.code === columnDef),
                    sectorXId,
                    iteratorXId: iterator.xId
                });
            return {
                ...acc,
                [columnDef]: columnData
            };
        }, {}
    );
}

function getFirstColumnData(iterator: INameXId): IServicesCellData {
    return {
        value: iterator.name,
        info: iterator.info
    };
}

function getServiceTableData({
    service, sectorXId, iteratorXId
}: { service: IServicesListGetterInput[]; sectorXId: string; iteratorXId: string; }): IServicesCellData {
    return {
        value: service.filter((serviceItem: IServicesListGetterInput) =>
            serviceItem.iterator && serviceItem.iterator.some((iterator: IXId) => iterator.xId === iteratorXId) &&
            serviceItem.sector && serviceItem.sector.some((sector: IXId) => sector.xId === sectorXId)
        ).length > 0
    };
}

function getColumnForGroup(
    serviceList: IServicesListGetterInput[], serviceEnum: IGetEnumsService[], servicesConfig: IServicesConfig, type: SectorXType)
    : IServicesTableColumns[] {
    const requiredColumns: IServicesTableColumns[] = Object.keys(servicesConfig)
        .filter((serviceCode: string) => serviceEnum.find((serviceItem: IGetEnumsService) => serviceItem.code === serviceCode))
        .map((serviceCode: string) => mapColumnItem(serviceCode, serviceEnum, servicesConfig, type));
    const columns: IServicesTableColumns[] = serviceList
        .filter((service: IServicesListGetterInput) => serviceEnum.find((serviceItem: IGetEnumsService) => serviceItem.code === service.code))
        .map((service: IServicesListGetterInput) => mapColumnItem(service.code, serviceEnum, servicesConfig, type));
    if (columns.length === 0 && requiredColumns.length === 0) {
        return columns;
    }
    return _uniqBy([...columns, ...requiredColumns], 'columnDef');
}

function mapColumnItem(serviceCode: string, serviceEnum: IGetEnumsService[], servicesConfig: IServicesConfig, type: SectorXType): IServicesTableColumns {
    return {
        columnDef: serviceCode,
        header: type === SectorXType.air ? serviceCode : serviceEnum.find((serviceItem: IGetEnumsService) => serviceItem.code === serviceCode).name,
        ...(type === SectorXType.air ? { codeTranslation: serviceEnum.find((serviceItem: IGetEnumsService) => serviceItem.code === serviceCode).name } : null),
        alertWhenMissing: servicesConfig[serviceCode] ? servicesConfig[serviceCode].alertWhenMissing : false
    };
}

function getColumnDefsList(groups: IServicesTableGroups[]): string[] {
    return groups.reduce((acc: string[], group: IServicesTableGroups) => {
        return [
            ...acc,
            ...group.columns.map((column: IServicesTableColumns) =>
                column.columnDef
            )
        ];
    }, []);
}

function capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
}